import { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, DataTable, FormField, Modal, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import { cn } from '@/lib/utils';
import { ACTIONS, ACTION_LABEL, DASHBOARDS, MODULES, SUBMODULES, type Action } from '@/lib/permissions';
import type { UtilisateurDTO } from '@/services/auth';
import {
  type UtilisateurInput,
  createUtilisateur,
  deleteUtilisateur,
  listUtilisateurs,
  updateUtilisateur,
} from '@/services/parametres';
import { useAuthStore } from '@/store/authStore';
import { toast } from '@/store/toastStore';
import type { UserRole } from '@/types';

const ROLES: UserRole[] = ['ADMIN', 'DIRECTEUR', 'DFC', 'CHEF_PROJET', 'CONDUCTEUR_TRAVAUX', 'CHEF_CHANTIER', 'ASSISTANTE_DIRECTION', 'COMPTABLE', 'RH', 'ACHETEUR', 'MAGASINIER', 'SECRETAIRE', 'LECTURE'];
const ROLE_LABEL: Record<UserRole, string> = {
  ADMIN: 'Administrateur (propriétaire)', DIRECTEUR: 'Directeur général', DFC: 'Directeur financier & comptable',
  CHEF_PROJET: 'Chef de projet', CONDUCTEUR_TRAVAUX: 'Conducteur de travaux', CHEF_CHANTIER: 'Chef de chantier',
  ASSISTANTE_DIRECTION: 'Assistant(e) de direction', COMPTABLE: 'Comptable', RH: 'Ressources humaines',
  ACHETEUR: 'Acheteur / approvisionnement', MAGASINIER: 'Magasinier (stock)', SECRETAIRE: 'Secrétaire', LECTURE: 'Lecture seule',
};

interface FormValues {
  nom: string; prenom: string; email: string; role: UserRole; password: string; is_active: boolean;
}

export function UtilisateursTab() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const { data = [], isLoading } = useQuery({ queryKey: ['utilisateurs'], queryFn: listUtilisateurs });
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [perms, setPerms] = useState<Record<string, string[]>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { register, handleSubmit, reset, watch } = useForm<FormValues>({ defaultValues: { role: 'LECTURE', is_active: true } });
  const role = watch('role');
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['utilisateurs'] });

  const toPayload = (v: FormValues): UtilisateurInput => ({
    nom: v.nom, prenom: v.prenom, email: v.email, role: v.role, is_active: !!v.is_active,
    permissions: v.role === 'ADMIN' ? {} : perms,
    ...(v.password ? { password: v.password } : {}),
  });
  const saveMutation = useMutation({
    mutationFn: (v: FormValues) => editId ? updateUtilisateur(editId, toPayload(v)) : createUtilisateur(toPayload(v)),
    onSuccess: () => { invalidate(); setModalOpen(false); toast.success(editId ? 'Utilisateur mis à jour.' : 'Utilisateur créé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({ mutationFn: deleteUtilisateur, onSuccess: () => { invalidate(); toast.success('Utilisateur supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  const openCreate = () => { setEditId(null); setPerms({}); reset({ nom: '', prenom: '', email: '', role: 'LECTURE', password: '', is_active: true }); setModalOpen(true); };
  const openEdit = (u: UtilisateurDTO) => { setEditId(u.id); setPerms(u.permissions ?? {}); reset({ nom: u.nom, prenom: u.prenom, email: u.email, role: u.role, password: '', is_active: u.is_active }); setModalOpen(true); };

  const has = (m: string, a: Action) => (perms[m] ?? []).includes(a);
  const toggle = (m: string, a: Action) => setPerms((p) => { const cur = new Set(p[m] ?? []); if (cur.has(a)) cur.delete(a); else cur.add(a); return { ...p, [m]: [...cur] }; });
  const toggleAll = (m: string, on: boolean) => setPerms((p) => ({ ...p, [m]: on ? [...ACTIONS] : [] }));
  // Sous-permission « module.sous » : AFFICHAGE de l'état EFFECTIF (hérite du parent si la
  // sous-clé n'est pas matérialisée — défaut additif, miroir du serveur).
  const effHas = (key: string, a: Action) =>
    key in perms ? (perms[key] ?? []).includes(a) : (perms[key.split('.')[0]] ?? []).includes(a);
  // 1er toggle d'une sous-clé non matérialisée : on la fige d'abord depuis l'héritage du parent,
  // puis on inverse l'action ciblée (retirer une action ne perd pas les autres héritées).
  const toggleSub = (parent: string, key: string, a: Action) => setPerms((p) => {
    const base = key in p ? p[key] : (p[parent] ?? []);
    const cur = new Set(base); if (cur.has(a)) cur.delete(a); else cur.add(a);
    return { ...p, [key]: [...cur] };
  });

  const columns: ColumnDef<UtilisateurDTO, unknown>[] = [
    { id: 'nom', header: 'Nom', accessorFn: (u) => `${u.prenom} ${u.nom}`, cell: ({ row }) => <span className="font-medium text-content-primary">{row.original.prenom} {row.original.nom}</span> },
    { accessorKey: 'email', header: 'Email' },
    { accessorKey: 'role', header: 'Rôle', size: 160, cell: ({ getValue }) => <span className="text-xs font-semibold text-brand-primary">{ROLE_LABEL[getValue() as UserRole] ?? String(getValue())}</span> },
    { id: 'modules', header: 'Modules', cell: ({ row }) => { const n = Object.values(row.original.permissions ?? {}).filter((a) => a.length).length; return row.original.role === 'ADMIN' ? <span className="text-xs text-content-muted">Tous (propriétaire)</span> : <span className="text-xs text-content-secondary">{n} module(s)</span>; } },
    { accessorKey: 'is_active', header: 'Statut', size: 100, cell: ({ getValue }) => getValue() ? <StatusBadge status="done" label="Actif" /> : <StatusBadge status="stuck" label="Inactif" /> },
    {
      id: 'actions', header: '', size: 90,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row.original)} aria-label="Modifier"><Pencil className="size-4" /></Button>
          {row.original.id !== currentUser?.id
            ? <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm(`Supprimer ${row.original.prenom} ${row.original.nom} ?`)) deleteMutation.mutate(row.original.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>
            : <span className="inline-flex h-8 w-8 items-center justify-center text-[10px] text-content-muted">vous</span>}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-content-secondary">Gérez les comptes et leurs <strong>permissions par module</strong>. La <strong>suppression</strong> de données est réservée au propriétaire (ADMIN).</p>
        <Button onClick={openCreate}><Plus /> Nouvel utilisateur</Button>
      </div>

      <DataTable columns={columns} data={data} loading={isLoading} pageSize={10} exportFileName="utilisateurs" />

      <Modal open={modalOpen} onOpenChange={setModalOpen} title={editId ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'} size="xl"
        footer={<><Button variant="secondary" onClick={() => setModalOpen(false)}>Annuler</Button><Button onClick={handleSubmit((v) => saveMutation.mutate(v))} loading={saveMutation.isPending}>{editId ? 'Enregistrer' : 'Créer'}</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Prénom" required><Input {...register('prenom')} /></FormField>
          <FormField label="Nom" required><Input {...register('nom')} /></FormField>
          <FormField label="Email" required className="sm:col-span-2"><Input type="email" {...register('email')} /></FormField>
          <FormField label="Rôle (intitulé)"><select {...register('role')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">{ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}</select></FormField>
          <FormField label={editId ? 'Nouveau mot de passe' : 'Mot de passe'} required={!editId} hint={editId ? 'Laisser vide pour ne pas changer' : '8 caractères minimum'}>
            <Input type="password" {...register('password')} autoComplete="new-password" placeholder={editId ? '••••••••' : ''} />
          </FormField>
        </div>

        {/* Accès aux tableaux de bord (permissions dédiées, assignables) */}
        <div className="mt-5">
          <p className="label-caps mb-2">Accès aux tableaux de bord</p>
          {role === 'ADMIN' ? (
            <p className="rounded-md border border-border bg-surface-primary px-4 py-3 text-sm text-content-secondary">Le rôle <strong>ADMIN</strong> a accès aux <strong>deux</strong> tableaux de bord.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {DASHBOARDS.map((d) => (
                <label key={d.permKey} className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-surface-primary px-3 py-2.5 hover:bg-surface-hover">
                  <input type="checkbox" className="mt-0.5 size-4 accent-brand-primary" checked={has(d.permKey, 'consulter')} onChange={() => toggle(d.permKey, 'consulter')} />
                  <span>
                    <span className="block text-sm font-medium text-content-primary">{d.label}</span>
                    <span className="block text-xs text-content-muted">{d.description}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Grille de permissions par module */}
        <div className="mt-5">
          <p className="label-caps mb-2">Permissions par module</p>
          {role === 'ADMIN' ? (
            <p className="rounded-md border border-border bg-surface-primary px-4 py-3 text-sm text-content-secondary">Le rôle <strong>ADMIN</strong> (propriétaire) a un accès total à tous les modules, y compris la suppression.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-primary/40 text-left text-xs text-content-muted">
                    <th className="px-3 py-2">Module</th>
                    {ACTIONS.map((a) => <th key={a} className="px-2 py-2 text-center">{ACTION_LABEL[a]}</th>)}
                    <th className="px-2 py-2 text-center">Tout</th>
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map((m) => {
                    const all = ACTIONS.every((a) => has(m.key, a));
                    const subs = SUBMODULES[m.key];
                    const open = !!expanded[m.key];
                    return (
                      <Fragment key={m.key}>
                        <tr className="border-b border-border/60 last:border-0">
                          <td className="px-3 py-1.5 font-medium text-content-primary">
                            <span className="flex items-center gap-1.5">
                              {subs ? (
                                <button type="button" onClick={() => setExpanded((e) => ({ ...e, [m.key]: !open }))} className="text-content-muted hover:text-content-primary" aria-expanded={open} aria-label={open ? 'Replier les onglets' : 'Déplier les onglets'}>
                                  <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
                                </button>
                              ) : <span className="inline-block w-3.5" />}
                              {m.label}
                            </span>
                          </td>
                          {ACTIONS.map((a) => (
                            <td key={a} className="px-2 py-1.5 text-center"><input type="checkbox" className="size-4 accent-brand-primary" checked={has(m.key, a)} onChange={() => toggle(m.key, a)} /></td>
                          ))}
                          <td className="px-2 py-1.5 text-center"><input type="checkbox" className="size-4 accent-brand-primary" checked={all} onChange={(e) => toggleAll(m.key, e.target.checked)} /></td>
                        </tr>
                        {/* Sous-grille dépliable : droits par ONGLET (sous-permission héritée du module si non cochée). */}
                        {subs && open && subs.map((sm) => {
                          const sall = ACTIONS.every((a) => effHas(sm.key, a));
                          return (
                            <tr key={sm.key} className="border-b border-border/40 bg-surface-primary/30 last:border-0">
                              <td className="py-1 pl-9 pr-3 text-xs text-content-secondary">{sm.label}</td>
                              {ACTIONS.map((a) => (
                                <td key={a} className="px-2 py-1 text-center"><input type="checkbox" className="size-3.5 accent-brand-primary" checked={effHas(sm.key, a)} onChange={() => toggleSub(m.key, sm.key, a)} /></td>
                              ))}
                              <td className="px-2 py-1 text-center"><input type="checkbox" className="size-3.5 accent-brand-primary" checked={sall} onChange={(e) => toggleAll(sm.key, e.target.checked)} /></td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-2 text-xs text-content-muted">Cliquez le chevron d'un module pour gérer les droits <strong>par onglet</strong> (sous-permission héritée du module si laissée cochée). La suppression de données n'est jamais déléguée : elle reste réservée au propriétaire (ADMIN).</p>
        </div>
      </Modal>
    </div>
  );
}
